// public/js/features/distribution/distribution-page.js
// Superadmin device distribution & inventory (docs/device-distribution.md).
// Two tabs: "Inventory" (per-tenant on-hand by model, filter, portfolio) and
// "Open requests" (a clean overview of every open request across all tenants).
// Model order everywhere: GRIFFIN-AIR first (flagship), then BLE beacons, then
// test devices last (hidden behind a "more" toggle, off by default).
import { loadDistribution, listOrders } from './api.js';
import { openRequestsModal } from './requests-modal.js';
import { openCreateOrderModal, openOrderDetailModal, statusPill } from './order-modal.js';
import { getCurrentUser, isPlatformLevel } from '../../modules/rbac.js';
import { formatPersonName } from '../../utils/person-name.js';
import { featureEnabled } from '../../modules/features.js';
// Deliberately NOT scoped by the global tenant selector: this is the platform's
// logistics view. Following the scope hid every tenant but the selected one
// while the tab badge kept counting all of them - "16 open, 1 shown".

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Test-only models nobody but the devs cares about — hidden by default, last.
// NOTE: only "Eddystone-TLM" is a test model; plain "Eddystone" is a standard beacon.
const TEST_MODEL_RX = /(barra|oyster|eddystone[\s_-]*tlm)/i;
const isTestModel = (m) => TEST_MODEL_RX.test(m);

// Global ordering rank: GRIFFIN-AIR (0) → BLE beacons & rest (1) → test (2).
function modelRank(m) {
  if (/griffin|air/i.test(m)) return 0;
  if (isTestModel(m)) return 2;
  return 1;
}
function orderModels(list) {
  return [...list].sort((a, b) => (modelRank(a) - modelRank(b)) || String(a).localeCompare(String(b)));
}

// A rough icon per model family — reused device iconography where it makes sense.
function modelIcon(model) {
  const m = String(model).toLowerCase();
  if (/griffin|air/.test(m)) return 'fa-solid fa-plane-up';        // GRIFFIN-AIR asset tracker
  if (/(barra|oyster|yabby|remora|dart|bolt|product|tracker|g[-_ ]?tracker)/.test(m)) return 'fa-solid fa-satellite-dish';
  if (/ela|rht|temp|humid/.test(m)) return 'fa-solid fa-temperature-half';
  if (/ingics|ibs|accel|movement|shock/.test(m)) return 'fa-solid fa-ruler-horizontal';
  if (/eddystone|beacon|ble|generic/.test(m)) return 'fa-solid fa-tag';
  return 'fa-solid fa-microchip';
}

const relTime = (iso) => {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString(navigator.language);
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(navigator.language, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return iso; } };
const prioBadge = (p) => `<span class="badge ${p >= 8 ? 'text-bg-danger' : p >= 4 ? 'text-bg-warning' : 'text-bg-secondary'}">P${p}</span>`;

let DATA = { models: [], tenants: [], totals: {}, requestedTotals: {}, openRequests: [] };
let selected = new Set();         // selected (shown) models
let showTestModels = false;       // reveal the test-model chips
let activeTab = 'inventory';      // 'inventory' | 'requests' | 'orders'
let ORDERS = [];                  // loaded with the orders tab

// The orders list is split four ways, and the groups do not overlap: an order
// handed to the carrier sits under Shipped, not under Open as well. OWN_GROUP
// lists every status that has a group of its own; "Open" is the catch-all for
// the rest, so a new status in the chain (ordered -> preparing -> shipped ->
// delivered) still lands somewhere by itself instead of disappearing.
const OWN_GROUP = ['shipped', 'delivered', 'cancelled'];
const ORDER_GROUPS = [
  { key: 'open', label: 'Open', icon: 'fa-solid fa-box-open',
    match: (o) => !OWN_GROUP.includes(o.status) },
  { key: 'shipped', label: 'Shipped', icon: 'fa-solid fa-truck-fast',
    match: (o) => o.status === 'shipped' },
  { key: 'delivered', label: 'Delivered', icon: 'fa-solid fa-circle-check',
    match: (o) => o.status === 'delivered' },
  { key: 'cancelled', label: 'Cancelled', icon: 'fa-solid fa-ban',
    match: (o) => o.status === 'cancelled' },
];
let orderGroup = 'open';
// One query PER GROUP, so a filter typed under Open is still there after a look
// at Shipped and back.
const orderQuery = { open: '', shipped: '', delivered: '', cancelled: '' };

// Each tab has its own deep-linkable address: #distribution/inventory and
// #distribution/requests (the router resolves sub-paths to the base page).
const TAB_IDS = ['inventory', 'requests', 'orders'];

function tabFromHash() {
  const sub = (window.location.hash || '').split('/')[1] || '';
  return TAB_IDS.includes(sub) ? sub : 'inventory';
}

// Reflect the active tab in the URL without re-triggering the router
// (replaceState fires no hashchange), so tab switches stay instant.
function syncHashToTab() {
  try { history.replaceState(null, '', `#distribution/${activeTab}`); } catch (e) {}
}

// Sum of open requested qty for a tenant, across a set of models.
function requestedTotal(t, models) {
  const req = t.requested || {};
  return models.reduce((s, m) => s + (req[m] || 0), 0);
}

export async function initDistributionPage(container) {
  const groups = getCurrentUser().groups || [];
  // Platform roles only - and that includes platformSupport, which the backend
  // has always allowed here (every distribution handler gates on isSuperAdmin,
  // which covers it). Support staff place orders and requests like anyone else
  // with access.
  if (!isPlatformLevel(groups)) {
    container.innerHTML = `<div class="alert alert-danger m-3">Access denied — platform roles only.</div>`;
    return;
  }

  // Deep link: #distribution/inventory | #distribution/requests.
  activeTab = tabFromHash();
  syncHashToTab();   // normalise a bare #distribution to the active tab's address

  container.innerHTML = `
    <div class="container-fluid py-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0"><i class="fa-solid fa-boxes-stacked me-2"></i>Device distribution &amp; inventory</h1>
        <button id="dist-refresh" class="btn btn-sm btn-outline-secondary"><i class="fa-solid fa-rotate me-1"></i>Refresh</button>
      </div>
      <ul class="nav nav-tabs mb-3" id="dist-tabs"></ul>
      <div id="dist-body"><div class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading inventory…</div></div>
    </div>`;

  container.querySelector('#dist-refresh')?.addEventListener('click', () => load(container));
  await load(container);
}

async function load(container) {
  const body = container.querySelector('#dist-body');
  try {
    DATA = await loadDistribution();
    // Default: show all non-test models (test devices start hidden AND off).
    selected = new Set(DATA.models.filter(m => !isTestModel(m)));
    renderAll(container);
  } catch (e) {
    body.innerHTML = `<div class="alert alert-danger">Failed to load inventory.</div>`;
  }
}

// Render tab bar + the active tab body.
function renderAll(container) {
  const openCount = (DATA.openRequests || []).length;
  const tabs = container.querySelector('#dist-tabs');
  tabs.innerHTML = `
    <li class="nav-item"><button class="nav-link ${activeTab === 'inventory' ? 'active' : ''}" data-tab="inventory"><i class="fa-solid fa-warehouse me-1"></i>Inventory</button></li>
    <li class="nav-item"><button class="nav-link ${activeTab === 'requests' ? 'active' : ''}" data-tab="requests"><i class="fa-solid fa-clipboard-list me-1"></i>Open requests${openCount ? ` <span class="badge text-bg-primary ms-1">${openCount}</span>` : ''}</button></li>
    <li class="nav-item"><button class="nav-link ${activeTab === 'orders' ? 'active' : ''}" data-tab="orders"><i class="fa-solid fa-truck-fast me-1"></i>Orders &amp; deliveries</button></li>`;
  tabs.querySelectorAll('[data-tab]').forEach(btn => btn.addEventListener('click', () => {
    activeTab = btn.dataset.tab;
    syncHashToTab();
    renderAll(container);
  }));

  const body = container.querySelector('#dist-body');
  if (activeTab === 'requests') renderOpenRequests(container, body);
  else if (activeTab === 'orders') renderOrders(container, body);
  else renderInventory(container, body);
}

// ============================ INVENTORY TAB ============================
function globalMax(models) {
  let mx = 1;
  for (const t of DATA.tenants) {
    for (const m of models) {
      const c = t.onHand[m];
      if (c) mx = Math.max(mx, c.inStock + c.deployed);
    }
  }
  return mx;
}

function bar(count, max, color) {
  const w = count > 0 ? Math.max(4, Math.round((count / max) * 100)) : 0;
  return `<span style="display:inline-block;height:10px;width:${w}%;background:${color};border-radius:2px;"></span>`;
}

function cellForModel(model, c, max) {
  const inStock = c?.inStock || 0;
  const deployed = c?.deployed || 0;
  if (inStock + deployed === 0) return '';
  return `
    <div class="d-flex align-items-center gap-2 mb-1" style="min-width:230px;" title="${esc(model)} — deployed ${deployed}, in stock ${inStock}">
      <i class="${modelIcon(model)} text-secondary" style="width:16px;text-align:center;"></i>
      <span class="small text-truncate" style="width:96px;">${esc(model)}</span>
      <span style="flex:1;display:inline-flex;gap:1px;align-items:center;background:var(--bs-secondary-bg,#eee);border-radius:2px;overflow:hidden;">
        ${bar(deployed, max, '#198754')}${bar(inStock, max, '#f59e0b')}
      </span>
      <span class="small fw-semibold" style="width:64px;text-align:right;">
        <span class="text-success">${deployed}</span><span class="text-muted">+</span><span class="text-warning">${inStock}</span>
      </span>
    </div>`;
}

function renderInventory(container, body) {
  const models = orderModels(DATA.models.filter(m => selected.has(m)));
  const max = globalMax(models);

  // Chips: non-test models, a "more" toggle, then the test chips when revealed.
  const nonTest = orderModels(DATA.models.filter(m => !isTestModel(m)));
  const test = orderModels(DATA.models.filter(isTestModel));
  const chip = (m) => `
    <label class="btn btn-sm ${selected.has(m) ? 'btn-primary' : 'btn-outline-secondary'} me-1 mb-1 dist-chip" data-model="${esc(m)}">
      <i class="${modelIcon(m)} me-1"></i>${esc(m)}
    </label>`;
  const moreBtn = test.length ? `
    <button class="btn btn-sm btn-outline-secondary me-1 mb-1" id="dist-more-models">
      <i class="fa-solid fa-flask me-1"></i>${showTestModels ? 'Hide' : 'More'} test models (${test.length})
    </button>` : '';
  const chips = nonTest.map(chip).join('') + moreBtn + (showTestModels ? test.map(chip).join('') : '');

  // Tenant rows (recompute total from the shown models only). The global
  // tenant scope narrows the table to the active tenant.
  const rows = DATA.tenants.map(t => {
    let tShown = 0;
    const cells = models.map(m => {
      const c = t.onHand[m];
      if (c) tShown += (c.inStock + c.deployed);
      return cellForModel(m, c, max);
    }).filter(Boolean).join('');
    return { t, tShown, reqOpen: requestedTotal(t, models), html: cells };
  }).filter(r => r.tShown > 0 || r.reqOpen > 0 || r.t.tenantId !== 'unassigned')
    .sort((a, b) => b.tShown - a.tShown);

  // Totals for the shown models — computed from the SCOPED tenant rows, so the
  // KPI strip and portfolio agree with the table under a single tenant scope.
  const scopedTenants = DATA.tenants;
  const totDeployed = {}, totStock = {};
  let grandStock = 0, grandDeployed = 0, grandRequested = 0;
  models.forEach(m => {
    const tt = scopedTenants.reduce((acc, t) => {
      const c = t.onHand[m];
      if (c) { acc.inStock += c.inStock; acc.deployed += c.deployed; }
      return acc;
    }, { inStock: 0, deployed: 0 });
    totDeployed[m] = tt.deployed; totStock[m] = tt.inStock;
    grandDeployed += tt.deployed; grandStock += tt.inStock;
    grandRequested += scopedTenants.reduce((s, t) => s + ((t.requested || {})[m] || 0), 0);
  });

  // KPI strip.
  const grandTotal = grandStock + grandDeployed;
  const tenantsWithStock = rows.filter(r => r.tShown > 0).length;
  const kpi = (label, value, cls) =>
    `<div class="col"><div class="border rounded p-2 h-100 text-center">
       <div class="h4 mb-0 fw-bold ${cls || ''}">${value}</div><div class="small text-muted">${label}</div>
     </div></div>`;
  const kpiStrip = `
    <div class="row row-cols-2 row-cols-md-6 g-2 mb-3">
      ${kpi('Devices', grandTotal, '')}
      ${kpi('Deployed', grandDeployed, 'text-success')}
      ${kpi('In stock', grandStock, 'text-warning')}
      ${kpi('Requested (open)', grandRequested, 'text-primary')}
      ${kpi('Tenants w/ stock', tenantsWithStock, '')}
      ${kpi('Models', `${models.length}/${DATA.models.length}`, '')}
    </div>`;

  // Fleet-by-model portfolio.
  const portMax = Math.max(1, ...models.map(m => totDeployed[m] + totStock[m]));
  const w = (n) => n > 0 ? `${Math.max(2, Math.round((n / portMax) * 100))}%` : '0';
  const portRow = (m) => {
    const dep = totDeployed[m], stk = totStock[m], req = DATA.requestedTotals?.[m] || 0;
    return `
      <div class="d-flex align-items-center gap-2 mb-2">
        <i class="${modelIcon(m)} text-secondary" style="width:18px;text-align:center;"></i>
        <span class="small text-truncate" style="width:120px;" title="${esc(m)}">${esc(m)}</span>
        <span style="flex:1;display:inline-flex;height:14px;background:var(--bs-secondary-bg,#eee);border-radius:3px;overflow:hidden;">
          <span style="width:${w(dep)};background:#198754;"></span><span style="width:${w(stk)};background:#f59e0b;"></span>
        </span>
        <span class="small fw-semibold text-nowrap" style="width:150px;text-align:right;">
          <span class="text-success">${dep}</span> · <span class="text-warning">${stk}</span>${req > 0 ? ` · <span class="badge text-bg-primary" title="open requests">+${req}</span>` : ''}
        </span>
      </div>`;
  };
  const portfolio = models.length ? `
    <div class="card shadow-sm mb-3">
      <div class="card-header py-2 small fw-semibold"><i class="fa-solid fa-chart-simple me-1"></i>Fleet by model</div>
      <div class="card-body py-3">${models.map(portRow).join('')}</div>
    </div>` : '';

  const totalsHtml = models.map(m =>
    `<div class="d-flex align-items-center gap-2 mb-1" style="min-width:230px;">
       <i class="${modelIcon(m)} text-secondary" style="width:16px;text-align:center;"></i>
       <span class="small text-truncate" style="width:96px;">${esc(m)}</span>
       <span class="small fw-semibold ms-auto"><span class="text-success">${totDeployed[m]}</span> deployed · <span class="text-warning">${totStock[m]}</span> stock</span>
     </div>`).join('');

  body.innerHTML = `
    ${kpiStrip}
    ${portfolio}
    <div class="mb-3" id="dist-chips">
      <div class="small text-secondary fw-semibold mb-1">Filter by model (click to toggle)</div>
      ${chips}
    </div>
    <div class="mb-2 small text-muted">
      <span class="me-3"><span style="display:inline-block;width:10px;height:10px;background:#198754;border-radius:2px;"></span> deployed (on an asset)</span>
      <span><span style="display:inline-block;width:10px;height:10px;background:#f59e0b;border-radius:2px;"></span> in stock (unassigned)</span>
    </div>
    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light"><tr>
            <th style="min-width:180px;">Tenant</th>
            <th>On hand (by model)</th>
            <th class="text-end" style="width:90px;">Total</th>
            <th class="text-center" style="width:150px;">Requested</th>
          </tr></thead>
          <tbody>
            ${rows.map(r => `
              <tr>
                <td class="fw-semibold">${esc(r.t.name)}</td>
                <td>${r.html || '<span class="text-muted small">—</span>'}</td>
                <td class="text-end fw-bold">${r.tShown}</td>
                <td class="text-center">
                  <button class="btn btn-sm ${r.reqOpen > 0 ? 'btn-outline-primary' : 'btn-outline-secondary'} dist-req-btn"
                          data-tenant-id="${esc(r.t.tenantId)}" data-tenant-name="${esc(r.t.name)}">
                    ${r.reqOpen > 0 ? `<span class="badge text-bg-primary me-1">${r.reqOpen}</span>open` : '<i class="fa-solid fa-plus me-1"></i>request'}
                  </button>
                </td>
              </tr>`).join('')}
          </tbody>
          <tfoot class="table-light">
            <tr>
              <td class="fw-bold">Totals</td>
              <td>${totalsHtml || '<span class="text-muted small">—</span>'}</td>
              <td class="text-end fw-bold">${grandTotal}</td>
              <td class="text-center fw-bold">${grandRequested > 0 ? `${grandRequested} open` : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>`;

  // Instant model filter.
  body.querySelectorAll('.dist-chip').forEach(chipEl => chipEl.addEventListener('click', () => {
    const m = chipEl.dataset.model;
    if (selected.has(m)) selected.delete(m); else selected.add(m);
    renderInventory(container, body);
  }));
  // Reveal / hide test models.
  body.querySelector('#dist-more-models')?.addEventListener('click', () => {
    showTestModels = !showTestModels;
    renderInventory(container, body);
  });
  // Per-tenant requests manager.
  body.querySelectorAll('.dist-req-btn').forEach(btn => btn.addEventListener('click', () => {
    openRequestsModal({ tenantId: btn.dataset.tenantId, name: btn.dataset.tenantName }, orderModels(DATA.models), () => load(container));
  }));
}

// ============================ OPEN REQUESTS TAB ============================
function renderOpenRequests(container, body) {
  const reqs = (DATA.openRequests || [])
    .sort((a, b) => (b.priority - a.priority) || String(a.requestedAt || '').localeCompare(String(b.requestedAt || '')));

  if (!reqs.length) {
    body.innerHTML = `
      <div class="card shadow-sm">
        <div class="card-body text-center text-muted py-5">
          <i class="fa-solid fa-clipboard-check fa-2x mb-3 d-block"></i>
          <div class="fw-semibold">No open requests</div>
          <div class="small">Everything requested has been delivered. Open a tenant's request manager from the Inventory tab to add one.</div>
        </div>
      </div>`;
    return;
  }

  const totalUnits = reqs.reduce((s, r) => s + (r.items || []).reduce((a, i) => a + (i.qty || 0), 0), 0);
  const tenantsWaiting = new Set(reqs.map(r => r.tenantId)).size;
  const topPriority = Math.max(...reqs.map(r => r.priority || 0));

  const kpi = (label, value, cls) =>
    `<div class="col"><div class="border rounded p-2 h-100 text-center">
       <div class="h4 mb-0 fw-bold ${cls || ''}">${value}</div><div class="small text-muted">${label}</div>
     </div></div>`;
  const kpiStrip = `
    <div class="row row-cols-2 row-cols-md-4 g-2 mb-3">
      ${kpi('Open requests', reqs.length, 'text-primary')}
      ${kpi('Units requested', totalUnits, '')}
      ${kpi('Tenants waiting', tenantsWaiting, '')}
      ${kpi('Top priority', topPriority ? `P${topPriority}` : '—', topPriority >= 8 ? 'text-danger' : topPriority >= 4 ? 'text-warning' : '')}
    </div>`;

  // Requested-by-model summary (bars), ordered GRIFFIN-AIR → beacons → test.
  const byModel = {};
  reqs.forEach(r => (r.items || []).forEach(i => { byModel[i.model] = (byModel[i.model] || 0) + (i.qty || 0); }));
  const modelList = orderModels(Object.keys(byModel));
  const bmMax = Math.max(1, ...Object.values(byModel));
  const bmRow = (m) => `
    <div class="d-flex align-items-center gap-2 mb-1">
      <i class="${modelIcon(m)} text-secondary" style="width:16px;text-align:center;font-size:0.8rem;"></i>
      <span class="small text-truncate" style="width:104px;" title="${esc(m)}">${esc(m)}</span>
      <span style="flex:1;display:inline-flex;height:9px;background:var(--bs-secondary-bg,#eee);border-radius:3px;overflow:hidden;">
        <span style="width:${Math.max(2, Math.round((byModel[m] / bmMax) * 100))}%;background:#0d6efd;"></span>
      </span>
      <span class="small fw-semibold text-primary" style="width:34px;text-align:right;">${byModel[m]}</span>
    </div>`;
  const byModelCard = modelList.length ? `
    <div class="col-12 col-lg-4">
      <div class="card shadow-sm">
        <div class="card-header py-2 small fw-semibold"><i class="fa-solid fa-layer-group me-1"></i>Requested by model</div>
        <div class="card-body py-2">${modelList.map(bmRow).join('')}</div>
      </div>
    </div>` : '';

  // Requests table (priority desc, then oldest).
  // A line that an order already drew from shows "open of requested".
  const itemChips = (items) => (items || []).map(i => {
    const open = Math.max(0, (i.qty || 0) - (i.ordered || 0));
    const qty = i.ordered ? `<span class="fw-semibold">${open}</span><span class="text-muted">/${i.qty}</span>×` : `<span class="fw-semibold">${i.qty}×</span>`;
    return `<span class="badge text-bg-light border me-1" title="${i.ordered ? `${i.ordered} already ordered` : ''}"><i class="${modelIcon(i.model)} me-1 text-secondary"></i>${qty} ${esc(i.model)}</span>`;
  }).join('') || '<span class="text-muted small">—</span>';
  const rowsHtml = reqs.map(r => `
    <tr>
      <td class="text-center">${prioBadge(r.priority)}</td>
      <td class="fw-semibold">${esc(r.tenantName)}${r.createdBy ? `<span class="d-block text-muted fw-normal small" title="${esc(r.createdBy)}">by ${esc(formatPersonName(r.createdBy))}</span>` : ''}</td>
      <td>${itemChips(r.items)}</td>
      <td class="text-nowrap small" title="${r.requestedAt ? new Date(r.requestedAt).toLocaleString(navigator.language) : ''}">${fmtDate(r.requestedAt)}<span class="text-muted d-block">${relTime(r.requestedAt)}</span></td>
      <td class="text-nowrap small">${r.targetDate ? `<i class="fa-solid fa-truck-fast text-secondary me-1"></i>${fmtDate(r.targetDate)}` : '<span class="text-muted">—</span>'}</td>
      <td class="small text-muted">${r.note ? esc(r.note) : '—'}</td>
      <td class="text-end text-nowrap">
        <button class="btn btn-sm btn-primary req-order" data-tenant-id="${esc(r.tenantId)}" data-tenant-name="${esc(r.tenantName)}" title="Create an order from this tenant's open requests">
          <i class="fa-solid fa-cart-plus me-1"></i>Order
        </button>
        <button class="btn btn-sm btn-outline-secondary req-manage" data-tenant-id="${esc(r.tenantId)}" data-tenant-name="${esc(r.tenantName)}" title="Open request manager">
          <i class="fa-solid fa-pen me-1"></i>Manage
        </button>
      </td>
    </tr>`).join('');

  const tableCard = `
    <div class="col-12 col-lg-8">
      <div class="card shadow-sm h-100">
        <div class="card-header py-2 small fw-semibold"><i class="fa-solid fa-list-check me-1"></i>All open requests</div>
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light"><tr>
              <th class="text-center" style="width:56px;">Prio</th>
              <th>Tenant</th>
              <th>Items</th>
              <th style="width:110px;">Requested</th>
              <th style="width:110px;">Wanted by</th>
              <th>Note</th>
              <th style="width:110px;"></th>
            </tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>
      </div>
    </div>`;

  body.innerHTML = `${kpiStrip}<div class="row g-3 align-items-start">${tableCard}${byModelCard}</div>`;

  body.querySelectorAll('.req-manage').forEach(btn => btn.addEventListener('click', () => {
    openRequestsModal({ tenantId: btn.dataset.tenantId, name: btn.dataset.tenantName }, orderModels(DATA.models), () => load(container));
  }));
  body.querySelectorAll('.req-order').forEach(btn => btn.addEventListener('click', () => {
    openCreateOrderModal({ tenantId: btn.dataset.tenantId, name: btn.dataset.tenantName }, DATA.openRequests || [], async () => {
      await load(container);
      activeTab = 'orders'; syncHashToTab(); renderAll(container);
    });
  }));
}

// ── Orders & deliveries tab ──────────────────────────────────────────────────

// What the per-tab search matches. Names first - tenant, consignee company and
// contact are what anyone actually looks for - but the order number, the city
// and the models are in there too, because typing "griffin" or "0007" is the
// same reflex.
const orderHaystack = (o) => [
  o.orderNo, o.tenantName, o.address?.name, o.address?.label, o.address?.contact,
  o.address?.city, o.address?.state, o.address?.country, o.tracking?.number, o.tracking?.carrier,
  ...(o.lines || []).map(l => l.model),
].filter(Boolean).join(' ').toLowerCase();

// A quiet marker in the order column: green cart = in the shop, amber = the
// push failed and the detail page offers a retry. Nothing at all where the
// environment cannot push (docs/shop-orders.md).
const shopMark = (o) => {
  if (!featureEnabled('shopOrders') || o.status === 'cancelled') return '';
  if (o.shop?.orderId) return ` <i class="fa-solid fa-cart-shopping text-success small" title="In the shop as order ${esc(o.shop.orderNo || o.shop.orderId)}"></i>`;
  if (o.shop?.queuedAt && !o.shop.error) return ` <i class="fa-solid fa-paper-plane text-primary small" title="Being sent to the shop"></i>`;
  return ` <i class="fa-solid fa-cart-shopping text-warning small" title="${esc(o.shop?.error || 'Not sent to the shop yet')}"></i>`;
};

const orderLineBadges = (o) => (o.lines || []).map(l =>
  `<span class="badge text-bg-light border me-1 mb-1"><i class="${modelIcon(l.model)} me-1 text-secondary"></i><span class="fw-semibold">${l.qty}×</span> ${esc(l.model)}</span>`).join('');

const orderTrackingLink = (o) => o.tracking?.number
  ? `<a href="${esc(o.tracking.url || '#')}" target="_blank" rel="noopener" class="order-track"><i class="fa-solid fa-truck-fast me-1"></i>${esc(o.tracking.number)}</a>`
  : '<span class="text-muted">—</span>';

// Wide screens get the table. Below md it becomes a stacked card per order:
// seven columns in a horizontally scrolling table is not a phone layout, and
// the columns that matter (who, what, how far along) all fit a card.
function ordersTableHtml(list) {
  const rows = list.map(o => `
    <tr class="order-row" data-id="${esc(o.orderId)}" style="cursor:pointer;">
      <td class="fw-semibold text-nowrap">${esc(o.orderNo)}${shopMark(o)}</td>
      <td>${statusPill(o.status)}</td>
      <td>${esc(o.tenantName)}<span class="d-block small text-muted">${esc(o.address?.city || '')} ${esc(o.address?.country || '')}</span></td>
      <td>${orderLineBadges(o)}</td>
      <td class="small text-nowrap">${(o.devices || []).length} / ${o.units || 0}</td>
      <td class="small">${orderTrackingLink(o)}</td>
      <td class="small text-nowrap text-muted" title="${o.createdAt ? new Date(o.createdAt).toLocaleString(navigator.language) : ''}">${fmtDate(o.createdAt)}</td>
    </tr>`).join('');
  return `
    <div class="table-responsive d-none d-md-block">
      <table class="table table-hover align-middle mb-0">
        <thead class="table-light"><tr><th>Order</th><th>Status</th><th>Tenant</th><th>Lines</th><th title="Units scanned into the box / units ordered">Scanned</th><th>Tracking</th><th>Created</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

function ordersCardsHtml(list) {
  return `<div class="d-md-none list-group list-group-flush">
    ${list.map(o => `
      <div class="list-group-item order-row" data-id="${esc(o.orderId)}" style="cursor:pointer;">
        <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
          <span class="fw-semibold">${esc(o.orderNo)}${shopMark(o)}</span>
          ${statusPill(o.status)}
        </div>
        <div class="small">${esc(o.tenantName)}</div>
        <div class="small text-muted mb-2">${esc(o.address?.city || '')} ${esc(o.address?.country || '')}</div>
        <div class="mb-1">${orderLineBadges(o)}</div>
        <div class="d-flex flex-wrap gap-3 small text-muted">
          <span title="Units scanned into the box / units ordered"><i class="fa-solid fa-barcode me-1"></i>${(o.devices || []).length} / ${o.units || 0}</span>
          <span>${orderTrackingLink(o)}</span>
          <span class="ms-auto">${fmtDate(o.createdAt)}</span>
        </div>
      </div>`).join('')}
  </div>`;
}

async function renderOrders(container, body) {
  body.innerHTML = `<div class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading orders…</div>`;
  try { ORDERS = await listOrders(); }
  catch (e) { body.innerHTML = `<div class="alert alert-danger">Failed to load orders.</div>`; return; }

  if (!ORDERS.length) {
    body.innerHTML = `
      <div class="card shadow-sm"><div class="card-body text-center text-muted py-5">
        <i class="fa-solid fa-truck-fast fa-2x mb-3 d-block"></i>
        <div class="fw-semibold">No orders yet</div>
        <div class="small">Create one from a tenant's open requests on the Open requests tab.</div>
      </div></div>`;
    return;
  }

  // Searching is a per-keystroke operation; building the haystack is not.
  ORDERS.forEach(o => { o.__hay = orderHaystack(o); });

  // In flight = open AND shipped, which is what "on the way" means. The first
  // and third tiles count exactly what the Open and Shipped pills count, so a
  // tile and its tab can never disagree - the mismatch this page already had
  // once, when the overview followed the tenant scope and the badge did not.
  const inFlight = ORDERS.filter(o => !['delivered', 'cancelled'].includes(o.status));
  const kpi = (label, value, cls) => `<div class="col"><div class="border rounded p-2 h-100 text-center"><div class="h4 mb-0 fw-bold ${cls || ''}">${value}</div><div class="small text-muted">${label}</div></div></div>`;
  const count = (st) => ORDERS.filter(o => o.status === st).length;
  const groupCount = (key) => ORDERS.filter(ORDER_GROUPS.find(g => g.key === key).match).length;
  const kpiStrip = `
    <div class="row row-cols-2 row-cols-md-4 g-2 mb-3">
      ${kpi('Open orders', groupCount('open'), 'text-primary')}
      ${kpi('Preparing', count('preparing'), 'text-warning')}
      ${kpi('In transit', groupCount('shipped'), '')}
      ${kpi('Units on the way', inFlight.reduce((s, o) => s + (o.units || 0), 0), '')}
    </div>`;

  // If the group the user was last on is now empty, fall back to the first one
  // that is not - landing on an empty "Cancelled" after a reload reads as a
  // broken page.
  if (!ORDERS.some(ORDER_GROUPS.find(g => g.key === orderGroup).match)) {
    orderGroup = (ORDER_GROUPS.find(g => ORDERS.some(g.match)) || ORDER_GROUPS[0]).key;
  }

  // The shell is painted ONCE. Typing re-renders only #order-list, so the
  // search box keeps focus and the caret stays where it was.
  body.innerHTML = `${kpiStrip}
    <div class="card shadow-sm">
      <div class="card-header py-2">
        <div class="d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
          <ul class="nav nav-pills nav-fill flex-wrap flex-sm-nowrap flex-grow-1 small" id="order-groups"></ul>
          <div class="input-group input-group-sm" style="max-width:22rem;">
            <span class="input-group-text bg-body"><i class="fa-solid fa-magnifying-glass text-muted"></i></span>
            <input id="order-search" class="form-control" type="search" autocomplete="off"
                   placeholder="Search tenant, consignee, order no…" aria-label="Search orders">
          </div>
        </div>
      </div>
      <div id="order-list"></div>
    </div>`;

  const groupsEl = body.querySelector('#order-groups');
  const listEl = body.querySelector('#order-list');
  const searchEl = body.querySelector('#order-search');

  const openDetail = (id) => {
    const o = ORDERS.find(x => x.orderId === id);
    if (o) openOrderDetailModal(o, () => renderOrders(container, body));
  };

  function paintList() {
    const group = ORDER_GROUPS.find(g => g.key === orderGroup);
    const q = (orderQuery[orderGroup] || '').trim().toLowerCase();
    const inGroup = ORDERS.filter(group.match);
    const list = q ? inGroup.filter(o => o.__hay.includes(q)) : inGroup;

    if (!list.length) {
      listEl.innerHTML = `<div class="text-center text-muted py-5">
        <i class="${group.icon} fa-2x mb-3 d-block opacity-50"></i>
        <div class="fw-semibold">${q ? 'Nothing matches that search' : `No ${group.label.toLowerCase()} orders`}</div>
        ${q ? `<div class="small">${inGroup.length} ${group.label.toLowerCase()} order${inGroup.length === 1 ? '' : 's'} in this tab — <button type="button" class="btn btn-link btn-sm p-0 align-baseline" id="order-search-clear">clear the search</button></div>` : ''}
      </div>`;
      listEl.querySelector('#order-search-clear')?.addEventListener('click', () => {
        orderQuery[orderGroup] = '';
        searchEl.value = '';
        searchEl.focus();
        paintList();
      });
      return;
    }

    listEl.innerHTML = ordersTableHtml(list) + ordersCardsHtml(list);
    listEl.querySelectorAll('.order-row').forEach(row => row.addEventListener('click', (e) => {
      if (e.target.closest('.order-track')) return;   // the tracking link opens the carrier, not the modal
      openDetail(row.dataset.id);
    }));
  }

  function paintGroups() {
    // The icons drop below sm. Three pills still measured 379px against a 379px
    // track at the narrowest tested width; a fourth does not fit, so the list
    // wraps to two rows below sm and only goes nowrap from sm up, where the
    // labels fit the track again.
    groupsEl.innerHTML = ORDER_GROUPS.map(g => {
      const n = ORDERS.filter(g.match).length;
      const on = g.key === orderGroup;
      return `<li class="nav-item"><button class="nav-link w-100 py-1 px-2 text-nowrap ${on ? 'active' : ''}" data-group="${g.key}">
        <i class="${g.icon} me-1 d-none d-sm-inline"></i>${g.label}
        <span class="badge ${on ? 'text-bg-light' : 'text-bg-secondary'} ms-1">${n}</span>
      </button></li>`;
    }).join('');
    groupsEl.querySelectorAll('[data-group]').forEach(btn => btn.addEventListener('click', () => {
      orderGroup = btn.dataset.group;
      searchEl.value = orderQuery[orderGroup] || '';
      paintGroups();
      paintList();
    }));
  }

  searchEl.value = orderQuery[orderGroup] || '';
  searchEl.addEventListener('input', () => {
    orderQuery[orderGroup] = searchEl.value;
    paintList();
  });

  paintGroups();
  paintList();
}
